import { AccessTime as AccessTimeIcon, LocationOn as LocationOnIcon, Photo as PhotoIcon } from "@mui/icons-material";
import { Chip as MuiChip, SxProps, Theme, useTheme } from "@mui/material";
import { Path, SupposedUsage } from "fileParsing/wrangler/types";
import React, { useMemo } from "react";
import { useDrag } from "react-dnd";

export const CHIP_TYPE = "chip";
export type ChipData = {
  path: Path;
  label: string;
  usage?: SupposedUsage;
  payload?: object;
};

export type ChipProps = {
  label: string;
  supposedUsage?: SupposedUsage;
  showIcon?: false;
  selected?: boolean;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  onClick?: () => void;
  onDelete?: () => void;
  hovered: boolean;
  onHover?: (mouseOver: boolean) => void;
};

type DragChipProps = {
  label: string;
  path: Path;
  usage?: SupposedUsage;
  selected?: boolean;
  sx?: SxProps<Theme>;
  onClick?: () => void;
  onDelete?: () => void;
  hovered: boolean;
  onHover?: (hovering?: boolean) => void;
};

export const DragChip: React.FC<DragChipProps> = ({
  label,
  path,
  usage,
  selected = false,
  sx,
  hovered,
  onHover,
  ...props
}) => {
  const [, dragRef] = useDrag<ChipData>(
    () => ({
      type: CHIP_TYPE,
      item: { path, label, usage },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      previewOptions: {
        captureDraggingState: false,
      },
    }),
    [],
  );

  return (
    <Chip
      ref={dragRef}
      label={label}
      supposedUsage={usage}
      selected={selected}
      size="small"
      sx={{
        width: "auto",
        height: "28px",
        //width: "80%",
        minWidth: "350px",
        transition: "all 0.3s ease-out",
        ...sx,
      }}
      hovered={hovered}
      onHover={onHover}
      {...props}
    />
  );
};

const getIcon = (usage: SupposedUsage | undefined) => {
  switch (usage?.format) {
    case "time":
      return <AccessTimeIcon />;
    case "uri":
      return <PhotoIcon />;
    case "lat":
    case "long":
      return <LocationOnIcon />;
    default:
      return undefined;
  }
};

export const Chip = React.forwardRef<HTMLDivElement, ChipProps>(function InnerChip(
  { label, supposedUsage, showIcon, selected = false, size = "small", sx, onClick, onDelete, hovered, onHover },
  ref,
) {
  const theme = useTheme();
  const background = hovered
    ? theme.palette.primary.main
    : selected
      ? theme.palette.secondary.main
      : theme.palette.grey[700];
  const icon = useMemo(() => (showIcon === undefined ? getIcon(supposedUsage) : undefined), [supposedUsage, showIcon]);

  return (
    <MuiChip
      ref={ref}
      size={size}
      key={label}
      icon={icon}
      label={label}
      onClick={onClick}
      onDelete={onDelete}
      sx={{
        background,
        fontWeight: "bold",
        cursor: onClick ? "pointer" : "grab",
        transition: "all 0.3s ease-out",
        "&:hover": { background },
        ...sx,
      }}
      onMouseMove={() => onHover && onHover(true)}
      onMouseLeave={() => onHover && onHover(false)}
    />
  );
});
